// ── "Job Number Assigned" email ──────────────────────────────────────────────
// Sent when Accounting assigns the job number (AssignJobNumberModal in
// Turnovers.jsx). Before this nothing went out at assignment: the Sales / BD
// rep had no prompt to put the number on the HubSpot deal (they own the deal —
// Marketing's 2026-09-24 correction), Marketing had no notice for project-sheet
// setup and internal tracking, and Sales didn't hear that
// the remaining sections had unlocked.
//
// Recipients = the Job Number Assignment Alert list (Turnover Configuration,
// GroupKey 'job_number_alert') + the turnover's salesperson and estimator.
// Pure: builds the message; the caller sends it. Every value is HTML-escaped.
import { sectionNumberOf } from "../../config/turnoverLayout";
import { KICKOFF_PARTS } from "../../config/marketingKickoff";

// Layout 3 (§18 Marketing): this email is also the call to action for
// the sales lead, the PM and the Marketing Director to coordinate §18 — the PM
// (when named on the Cover Sheet) and the Director are added to CC, and the
// Marketing block carries a pre-filled Outlook "working session" invite.
// Marketing Director = the Director row of Turnover Configuration's Marketing
// Team list (GroupKey 'marketing_team', RoleLabel 'Director').
export const MARKETING_DIRECTOR_ROLE = "Director";
export function marketingDirector(configContacts = []) {
  const d = (configContacts || []).find(c => c.GroupKey === "marketing_team" && c.RoleLabel === MARKETING_DIRECTOR_ROLE && c.Email?.trim());
  return d ? { name: d.Name || d.Email.trim(), email: d.Email.trim() } : null;
}

// Outlook on the web "new meeting", pre-filled with subject, body and attendees.
export function outlookMeetingLink({ subject, body, attendees = [] }) {
  const q = new URLSearchParams({ subject, body, to: attendees.map(a => a.email).join(",") });
  return `https://outlook.office.com/calendar/deeplink/compose?${q.toString()}`;
}

export const HUBSPOT_PORTAL_ID = "44218274";
export const PLATFORM_BASE_URL = "https://estimating.cleanspaceus.com";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function hubspotDealUrl(dealId) {
  const id = String(dealId ?? "").trim();
  return /^\d+$/.test(id) ? `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${id}` : null;
}

// configContacts: sp_GetTurnoverConfigContacts rows. To = the alert's To row;
// CC = its CC rows + salesperson + estimator. Case-insensitive de-dupe, and a
// person already on To is never repeated in CC.
// opts.pm = { name, email } of the Cover Sheet's PM, when known (layout 3).
export function jobNumberAssignedRecipients(configContacts = [], turnover = {}, opts = {}) {
  const alert = (configContacts || []).filter(c => c.GroupKey === "job_number_alert" && c.Email?.trim());
  const seen = new Set();
  const take = (name, email) => {
    const e = String(email || "").trim();
    if (!e || seen.has(e.toLowerCase())) return null;
    seen.add(e.toLowerCase());
    return { name: name || e, email: e };
  };
  const to = alert.filter(c => c.RoleLabel === "To").map(c => take(c.Name, c.Email)).filter(Boolean);
  const cc = [
    ...alert.filter(c => c.RoleLabel === "CC").map(c => take(c.Name, c.Email)),
    take(turnover.Salesperson, turnover.SalespersonEmail),
    take(turnover.Estimator, turnover.EstimatorEmail),
    ...(sectionNumberOf(turnover, "marketing") ? [
      opts.pm ? take(opts.pm.name, opts.pm.email) : null,
      (() => { const d = marketingDirector(configContacts); return d ? take(d.name, d.email) : null; })(),
    ] : []),
  ].filter(Boolean);
  // No To configured: promote the first CC so the message still has a To.
  if (to.length === 0 && cc.length > 0) to.push(cc.shift());
  return { to, cc };
}

export function buildJobNumberAssignedEmail({ turnover = {}, jobNumber, assignedBy = "", pm = null, director = null }) {
  const project = esc(turnover.ProjectName) || "this project";
  const job = esc(jobNumber);
  const dealUrl = hubspotDealUrl(turnover.HubSpotDealID);
  const turnoverUrl = `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnover.TurnoverID, 10) || ""}`;
  // Layout 3+: Marketing's block opens §18 Marketing.
  const kickoffNum = sectionNumberOf(turnover, "marketing");
  const td = "padding:6px 12px;border:1px solid #e5e7eb;font-size:13px";
  const th = `${td};background:#f8f9fb;font-weight:600;width:160px`;
  const btn = (href, label, bg) =>
    `<a href="${esc(href)}" style="display:inline-block;background:${bg};color:#fff;padding:10px 22px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none;margin:0 8px 8px 0">${label}</a>`;
  // Layout 3: sales lead + PM + Marketing Director coordinate §18.
  function kickoffBlock() {
    const kickoffUrl = `${turnoverUrl}?section=${kickoffNum}`;
    const pmName = String(pm?.name || turnover.ProjectManager || "").trim();
    const attendees = [{ email: turnover.SalespersonEmail }, pm, director].filter(a => a?.email);
    const meeting = outlookMeetingLink({
      subject: `Marketing — ${turnover.ProjectName || "Turnover"}`,
      body: `Coordinate Section ${kickoffNum}, Marketing, for ${turnover.ProjectName || "the project"}: ${kickoffUrl}`,
      attendees,
    });
    const who = [esc(turnover.Salesperson) || "the sales lead", pmName ? esc(pmName) : "the Project Manager", director ? esc(director.name) : "Marketing"];
    return `<div style="background:#f5f3ff;border:1px solid #c4b5fd;border-radius:6px;padding:14px 16px;margin:0 0 18px">
      <div style="font-weight:700;color:#5b21b6;margin-bottom:6px">Sales, PM &amp; Marketing — coordinate the Marketing section</div>
      <div style="margin-bottom:10px">${who[0]}, ${who[1]} and ${who[2]}: set up a short working session to go through Section ${kickoffNum}, Marketing. It has to be done before the package is complete and is reviewed at the turnover meeting.${pmName ? "" : " No Project Manager is named yet — add them to the invite once the VP of Construction assigns one."} Marketing: the job number is also for project-sheet setup; no HubSpot update is needed from Marketing.</div>
      <div style="font-size:12.5px;color:#4c1d95;margin-bottom:12px">${KICKOFF_PARTS.map(p => `${esc(p.label)} — ${esc(p.lead)}`).join(" · ")}</div>
      ${btn(meeting, "📅 Schedule a working session →", "#5b21b6")}
      ${btn(kickoffUrl, "Open the Marketing section →", "#1e3a5f")}
    </div>`;
  }

  const html = `
<div style="font-family:Arial,sans-serif;max-width:680px;color:#111">
  <div style="background:#1e3a5f;padding:18px 26px;border-radius:8px 8px 0 0">
    <div style="color:#fff;font-size:18px;font-weight:700">Job Number Assigned — ${job}</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:4px">${project}${turnover.ClientName ? ` — ${esc(turnover.ClientName)}` : ""}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;padding:22px 26px;font-size:14px;line-height:1.55">
    <p style="margin:0 0 16px">${assignedBy ? `${esc(assignedBy)} assigned` : "Accounting assigned"} job number <strong>${job}</strong> to <strong>${project}</strong>.</p>

    <div style="background:#fff7ed;border:1px solid #fdba74;border-radius:6px;padding:14px 16px;margin:0 0 18px">
      <div style="font-weight:700;color:#9a3412;margin-bottom:6px">${esc(turnover.Salesperson) || "Sales / BD rep"} — action needed</div>
      <div style="margin-bottom:12px">Open the HubSpot deal and enter <strong>${job}</strong> in its <strong>job number</strong> field. The Sales / BD rep who owns the deal keeps it up to date.</div>
      ${dealUrl
        ? btn(dealUrl, "Open the HubSpot deal →", "#ff7a59")
        : `<div style="color:#9a3412;font-size:13px">This turnover has no linked HubSpot deal — find the deal for ${project} in HubSpot.</div>`}
    </div>

    <div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:6px;padding:14px 16px;margin:0 0 18px">
      <div style="font-weight:700;color:#075985;margin-bottom:6px">Sales &amp; Estimating — the turnover is unlocked</div>
      <div style="margin-bottom:12px">Document uploads for the remaining sections (4 onward) are now open. Each section's owner should upload their documents (or mark the section N/A) so the package can be completed and turned over.</div>
      ${btn(turnoverUrl, "Open the turnover →", "#1e3a5f")}
    </div>

    ${kickoffNum ? kickoffBlock() : `<div style="background:#f5f3ff;border:1px solid #c4b5fd;border-radius:6px;padding:12px 16px;margin:0 0 18px">
      <div style="font-weight:700;color:#5b21b6;margin-bottom:4px">Marketing — for your records</div>
      <div>Job number for project-sheet creation and internal tracking. No HubSpot update is needed from Marketing.</div>
    </div>`}

    <table style="width:100%;border-collapse:collapse;margin:0 0 6px">
      <tr><td style="${th}">Job #</td><td style="${td}">${job}</td></tr>
      <tr><td style="${th}">Project</td><td style="${td}">${project}</td></tr>
      <tr><td style="${th}">Client</td><td style="${td}">${esc(turnover.ClientName) || "—"}</td></tr>
      <tr><td style="${th}">Sales Lead</td><td style="${td}">${esc(turnover.Salesperson) || "—"}</td></tr>
      <tr><td style="${th}">HubSpot Deal #</td><td style="${td}">${esc(turnover.HubSpotDealID) || "—"}</td></tr>
    </table>
  </div>
</div>`;

  return {
    subject: `Job Number Assigned — ${String(jobNumber || "").trim()} — ${turnover.ProjectName || "Turnover"} (update HubSpot)`,
    html,
  };
}
